// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, AlertCircle } from 'lucide-react';

const ClaimCoachProfile: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [coachRecord, setCoachRecord] = useState<any>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError('Invalid or missing token');
      setLoading(false);
    }
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('coach_import_records')
        .select('*')
        .eq('magic_link_token', token)
        .single();

      if (error) throw new Error('Invalid token');

      // Check if already claimed
      if (data.claimed) {
        setError('This profile has already been claimed');
        setLoading(false);
        return;
      }

      // Check if expired
      if (new Date(data.magic_link_expires_at) < new Date()) {
        setError('This invitation link has expired');
        setLoading(false);
        return;
      }

      setCoachRecord(data);
    } catch (error: any) {
      console.error('Token validation error:', error);
      setError(error.message || 'Invalid token');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive',
      });
      return;
    }

    setClaiming(true);

    try {
      // Look up sport_id from sport_name
      let sportId = null;
      if (coachRecord.sport_name && coachRecord.gender) {
        const { data: sportData } = await supabase
          .from('sports')
          .select('id')
          .eq('name', coachRecord.sport_name)
          .eq('gender', coachRecord.gender)
          .single();
        
        sportId = sportData?.id || null;
      }

      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: coachRecord.email,
        password: password,
        options: {
          data: {
            name: coachRecord.name,
            role: 'college_coach',
          },
        },
      });

      if (authError) throw authError;

      // Wait a bit for any auth triggers to complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update profile (it may have been created by handle_new_user trigger)
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', authData.user!.id)
        .single();

      let profileData;
      if (existingProfile) {
        // Update existing profile
        const { data, error: profileError } = await supabase
          .from('profiles')
          .update({
            email: coachRecord.email,
            name: coachRecord.name,
            role: 'college_coach',
            college_name: coachRecord.college_name,
            sport_id: sportId,
            division: coachRecord.division,
            state: coachRecord.state,
          })
          .eq('user_id', authData.user!.id)
          .select()
          .single();

        if (profileError) throw profileError;
        profileData = data;
      } else {
        // Create new profile
        const { data, error: profileError } = await supabase
          .from('profiles')
          .insert({
            user_id: authData.user!.id,
            email: coachRecord.email,
            name: coachRecord.name,
            role: 'college_coach',
            college_name: coachRecord.college_name,
            sport_id: sportId,
            division: coachRecord.division,
            state: coachRecord.state,
          })
          .select()
          .single();

        if (profileError) throw profileError;
        profileData = data;
      }

      // Update coach import record
      const { error: updateError } = await supabase
        .from('coach_import_records')
        .update({
          claimed: true,
          claimed_at: new Date().toISOString(),
          profile_id: profileData.id,
        })
        .eq('id', coachRecord.id);

      if (updateError) throw updateError;

      // Cancel any pending sequence emails for this coach
      console.log('🚫 Cancelling pending sequence emails for:', coachRecord.email);
      await supabase
        .from('communication_logs')
        .update({ status: 'cancelled' })
        .eq('recipient_email', coachRecord.email)
        .eq('status', 'pending')
        .in('metadata->>trigger_source', ['coach_import']);

      console.log('✅ Cancelled pending sequence emails');

      // Update import stats
      const { data: importData } = await supabase
        .from('coach_imports')
        .select('profiles_claimed')
        .eq('id', coachRecord.import_id)
        .single();

      await supabase
        .from('coach_imports')
        .update({
          profiles_claimed: (importData?.profiles_claimed || 0) + 1,
        })
        .eq('id', coachRecord.import_id);

      // Send instant notification to Nancy about the profile claim
      try {
        await supabase
          .from('communication_logs')
          .insert({
            type: 'email',
            recipient_email: 'nancy@zw2.com',
            subject: `🎉 New Coach Claimed Profile: ${coachRecord.name}`,
            content: `
              <html>
                <body style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #002E62;">New Coach Profile Claimed!</h2>
                  <div style="background: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
                    <p style="margin: 8px 0;"><strong>Name:</strong> ${coachRecord.name}</p>
                    <p style="margin: 8px 0;"><strong>Email:</strong> ${coachRecord.email}</p>
                    <p style="margin: 8px 0;"><strong>School:</strong> ${coachRecord.college_name || 'Not provided'}</p>
                    <p style="margin: 8px 0;"><strong>Sport:</strong> ${coachRecord.sport_name || 'Not provided'}</p>
                    <p style="margin: 8px 0;"><strong>Division:</strong> ${coachRecord.division || 'Not provided'}</p>
                    <p style="margin: 8px 0;"><strong>State:</strong> ${coachRecord.state || 'Not provided'}</p>
                    <p style="margin: 8px 0;"><strong>Claimed At:</strong> ${new Date().toLocaleString()}</p>
                  </div>
                  <p style="margin-top: 20px;">
                    <a href="${window.location.origin}/admin/coach-imports" 
                       style="background: #002E62; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
                      View in Coach Management →
                    </a>
                  </p>
                </body>
              </html>
            `,
            status: 'pending',
            metadata: {
              trigger_source: 'coach_profile_claimed',
              coach_email: coachRecord.email,
              coach_name: coachRecord.name,
              profile_id: profileData.id
            },
            created_at: new Date().toISOString()
          });
        
        console.log('✅ Admin notification queued for coach claim');
      } catch (notifyError) {
        console.error('Failed to queue admin notification:', notifyError);
        // Don't fail the entire claim process if notification fails
      }

      toast({
        title: 'Success!',
        description: 'Your profile has been claimed',
      });

      // Redirect to dashboard
      setTimeout(() => {
        navigate('/college-coach-dashboard');
      }, 2000);
    } catch (error: any) {
      console.error('Claim profile error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to claim profile',
        variant: 'destructive',
      });
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">Loading...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="w-12 h-12 mx-auto text-destructive" />
              <h2 className="text-xl font-bold">Invalid Link</h2>
              <p className="text-muted-foreground">{error}</p>
              <Button onClick={() => navigate('/')}>Go to Home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Claim Your Coach Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Coach Info */}
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <p><strong>Name:</strong> {coachRecord.name}</p>
              <p><strong>Email:</strong> {coachRecord.email}</p>
              <p><strong>College:</strong> {coachRecord.college_name}</p>
              <p><strong>Sport:</strong> {coachRecord.sport_name}</p>
            </div>

            {/* Password Form */}
            <form onSubmit={handleClaimProfile} className="space-y-4">
              <div>
                <Label htmlFor="password">Create Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              <div>
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              <Button type="submit" className="w-full" disabled={claiming}>
                <CheckCircle className="w-4 h-4 mr-2" />
                {claiming ? 'Creating Profile...' : 'Claim Profile'}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClaimCoachProfile;
